import { useEffect } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import {
  BuildingOffice2Icon,
  CubeTransparentIcon,
  PencilSquareIcon,
  WrenchScrewdriverIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";

const services = [
  {
    icon: BuildingOffice2Icon,
    title: "Civil Consultancy",
    description: "Expert civil consultancy services providing innovative solutions for infrastructure development.",
  },
  {
    icon: CubeTransparentIcon,
    title: "Structural Engineering",
    description: "Safety-critical structural design ensuring durability and resilience.",
  },
  {
    icon: PencilSquareIcon,
    title: "Architectural Design",
    description: "Aesthetic functionalism blending art with utility.",
  },
  {
    icon: WrenchScrewdriverIcon,
    title: "Turnkey Construction",
    description: "End-to-end project management from soil to skyline.",
  },
];

const HomePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-zinc-50"
    >
      <Navbar isHeroPage={true} />
      <main>
        <HeroSection />
        
        {/* Services Preview */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider font-manrope">
                What We Do
              </span>
              <h2 className="font-oswald text-3xl sm:text-4xl font-bold text-slate-900 mt-4">
                Our <span className="text-orange-500">Services</span>
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((service, index) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-zinc-50 border border-slate-100 p-6 hover:shadow-lg transition-all group"
                >
                  <div className="w-12 h-12 bg-orange-500 flex items-center justify-center mb-4 group-hover:bg-slate-900 transition-colors">
                    <service.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-oswald text-lg font-semibold text-slate-900 mb-2">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 text-sm font-manrope">
                    {service.description}
                  </p>
                </motion.div>
              ))}
            </div>
            
            <div className="text-center mt-10">
              <Link
                to="/services"
                className="inline-flex items-center gap-2 text-orange-500 font-semibold font-manrope hover:text-orange-600 transition-colors"
                data-testid="view-all-services"
              >
                View All Services
                <ArrowRightIcon className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
              {[
                { number: "150+", label: "Projects Delivered" },
                { number: "100+", label: "Happy Clients" },
                { number: "50+", label: "Team Members" },
                { number: "12+", label: "Years Experience" },
              ].map((stat) => (
                <div key={stat.label}>
                  <p className="font-oswald text-4xl font-bold text-white">
                    {stat.number.replace("+", "")}
                    <span className="text-orange-500">+</span>
                  </p>
                  <p className="text-slate-400 mt-2 font-manrope">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-zinc-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-oswald text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              Ready to Start Your <span className="text-orange-500">Project?</span>
            </h2>
            <p className="text-slate-600 font-manrope mb-8 text-lg">
              Contact us today for a free consultation and let's build something amazing together.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-orange-500 text-white px-8 py-4 font-manrope font-semibold uppercase tracking-wider text-sm hover:bg-orange-600 transition-colors"
              data-testid="home-cta"
            >
              Get Free Quote
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </motion.div>
  );
};

export default HomePage;
